import axios from "../axios";

// Get all published blogs
export const getPublishedBlogs = async () => {
  try {
    const response = await axios.get("/blogs/published");
    // Backend returns { message: 'Published blogs fetched successfully', blogs: [...] }
    if (response.data?.blogs && Array.isArray(response.data.blogs)) {
      return response.data.blogs;
    } else if (Array.isArray(response.data)) {
      return response.data;
    }
    console.warn("Unexpected response structure:", response.data);
    return [];
  } catch (error) {
    console.error("Failed to fetch published blogs:", error);
    const message =
      error.response?.data?.message ||
      error.response?.data?.error?.message ||
      "Failed to fetch blogs. Please try again.";
    throw new Error(message);
  }
};

// Get blog by ID or slug
export const getBlogById = async (idOrSlug) => {
  try {
    const response = await axios.get(`/blogs/${idOrSlug}`);
    // Backend returns { message: 'Blog fetched successfully', blog: {...} }
    if (response.data?.blog) {
      return response.data.blog;
    } else if (response.data) {
      return response.data;
    }
    throw new Error("Blog data not found in response");
  } catch (error) {
    console.error("Error fetching blog:", error);
    const message =
      error.response?.data?.message ||
      error.response?.data?.error?.message ||
      "Failed to fetch blog. Please try again.";
    throw new Error(message);
  }
};

// Create a comment on a blog
export const createComment = async (blogId, commentData) => {
  try {
    const response = await axios.post(`/blogs/comments/${blogId}`, commentData);
    // Backend returns { message, commentId, approved }
    return response.data;
  } catch (error) {
    console.error("Error creating comment:", error);
    const message =
      error.response?.data?.message ||
      error.response?.data?.error?.message ||
      "Failed to add comment. Please try again.";
    throw new Error(message);
  }
};

// Create a new blog post
export const createBlog = async (blogData) => {
  try {
    const response = await axios.post("/blogs", blogData);
    // Backend returns { message: "Blog created successfully", blogId }
    return response.data;
  } catch (error) {
    console.error("Error creating blog:", error);
    const message =
      error.response?.data?.message ||
      error.response?.data?.error?.message ||
      "Failed to create blog. Please try again.";
    throw new Error(message);
  }
};

// Get all blogs (admin only)
export const getAllBlogs = async () => {
  try {
    const response = await axios.get("/blogs/all");
    // Backend returns { message: 'All blogs fetched successfully', blogs: [...] }
    if (response.data?.blogs && Array.isArray(response.data.blogs)) {
      return response.data.blogs;
    } else if (Array.isArray(response.data)) {
      return response.data;
    }
    console.warn("Unexpected response structure:", response.data);
    return [];
  } catch (error) {
    console.error("Failed to fetch all blogs:", error);
    const message =
      error.response?.data?.message ||
      error.response?.data?.error?.message ||
      "Failed to fetch blogs. Please try again.";
    throw new Error(message);
  }
};

// Update a blog post
export const updateBlog = async (blogId, blogData) => {
  try {
    const response = await axios.put(`/blogs/${blogId}`, blogData);
    // Backend returns { message: 'Blog updated successfully' }
    return response.data;
  } catch (error) {
    console.error("Error updating blog:", error);
    const message =
      error.response?.data?.message ||
      error.response?.data?.error?.message ||
      "Failed to update blog. Please try again.";
    throw new Error(message);
  }
};

// Delete a blog post
export const deleteBlog = async (blogId) => {
  try {
    const response = await axios.delete(`/blogs/${blogId}`);
    // Backend returns { message: 'Blog deleted successfully' }
    return response.data;
  } catch (error) {
    console.error("Error deleting blog:", error);
    const message =
      error.response?.data?.message ||
      error.response?.data?.error?.message ||
      "Failed to delete blog. Please try again.";
    throw new Error(message);
  }
};

// Get all comments (admin only)
export const getAllComments = async (params = {}) => {
  try {
    const { approved, deleted, limit = 20, offset = 0 } = params;
    const queryParams = new URLSearchParams();
    
    if (approved !== undefined) {
      queryParams.append('approved', approved);
    }
    if (deleted !== undefined) {
      queryParams.append('deleted', deleted);
    }
    queryParams.append('limit', limit);
    queryParams.append('offset', offset);
    
    const response = await axios.get(`/blogs/comments?${queryParams.toString()}`);
    // Backend returns { comments: [...] }
    return response.data.comments || [];
  } catch (error) {
    console.error("Error fetching comments:", error);
    const message =
      error.response?.data?.message ||
      error.response?.data?.error?.message ||
      "Failed to fetch comments. Please try again.";
    throw new Error(message);
  }
};

// Bulk moderate comments
export const bulkModerateComments = async (commentIds, action) => {
  try {
    const response = await axios.put('/blogs/comments/bulk-moderate', {
      commentIds,
      action,
    });
    // Backend returns { message: 'Action completed', affectedRows }
    return response.data;
  } catch (error) {
    console.error("Error bulk moderating comments:", error);
    const message =
      error.response?.data?.message ||
      error.response?.data?.error?.message ||
      "Failed to moderate comments. Please try again.";
    throw new Error(message);
  }
};

// Normalize blog data for list display
export function normalizeBlogForList(blog) {
  // Parse categories if it's a JSON string
  let categories = [];
  try {
    categories = typeof blog.categories === 'string' 
      ? JSON.parse(blog.categories) 
      : (Array.isArray(blog.categories) ? blog.categories : []);
  } catch (e) {
    categories = [];
  }

  // Get first category or default
  const category = categories[0] || blog.category || "Uncategorized";

  // Extract image URL from blog.images array
  // Backend stores Cloudinary URLs in blog_images table like: "https://res.cloudinary.com/dqxpvhrin/image/upload/v1754221873/samples/people/boy-snow-hoodie.jpg"
  // Images can be returned as array of objects with 'image' property or array of URL strings
  let image = null;
  
  if (blog.images && Array.isArray(blog.images) && blog.images.length > 0) {
    const firstImage = blog.images[0];
    
    // Handle array of objects: [{image: "https://res.cloudinary.com/..."}, ...]
    if (typeof firstImage === 'object' && firstImage !== null && !Array.isArray(firstImage)) {
      // Check for 'image' property (most common from database)
      if (firstImage.image && typeof firstImage.image === 'string' && firstImage.image.trim()) {
        image = firstImage.image.trim();
      }
      // Check for 'url' property as fallback
      else if (firstImage.url && typeof firstImage.url === 'string' && firstImage.url.trim()) {
        image = firstImage.url.trim();
      }
    }
    // Handle array of strings: ["https://res.cloudinary.com/...", ...]
    else if (typeof firstImage === 'string' && firstImage.trim()) {
      image = firstImage.trim();
    }
  }
  
  // Fallback to blog.image if it exists (direct property, could be Cloudinary URL)
  if (!image && blog.image && typeof blog.image === 'string' && blog.image.trim()) {
    image = blog.image.trim();
  }
  
  // Validate image URL - must be a valid Cloudinary URL (https://) or relative path (/)
  // Cloudinary URLs format: https://res.cloudinary.com/{cloud_name}/image/upload/{version}/{path}
  if (!image || image === "" || (!image.startsWith('http://') && !image.startsWith('https://') && !image.startsWith('/'))) {
    image = "/placeholder.svg";
  }

  // Format date
  const date = blog.createdAt 
    ? new Date(blog.createdAt).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

  // Get description from metaDescription or excerpt, or generate from content
  let description = blog.metaDescription || blog.excerpt || blog.description;
  if (!description && blog.content) {
    // Strip HTML tags and get first 150 characters
    const textContent = blog.content.replace(/<[^>]*>/g, "").trim();
    description = textContent.length > 150 
      ? textContent.substring(0, 150) + "..." 
      : textContent;
  }

  return {
    id: blog.id,
    title: blog.title,
    description: description || "No description available",
    image: image,
    date: date,
    category: category,
    slug: blog.slug,
    createdAt: blog.createdAt,
  };
}

// Normalize blog data for detail display
export function normalizeBlogForDetail(blog) {
  // Parse tags if it's a JSON string
  let tags = [];
  try {
    tags = typeof blog.tags === 'string' 
      ? JSON.parse(blog.tags) 
      : (Array.isArray(blog.tags) ? blog.tags : []);
  } catch (e) {
    tags = [];
  }

  // Parse categories if it's a JSON string
  let categories = [];
  try {
    categories = typeof blog.categories === 'string' 
      ? JSON.parse(blog.categories) 
      : (Array.isArray(blog.categories) ? blog.categories : []);
  } catch (e) {
    categories = [];
  }

  // Get first category or default
  const category = categories[0] || blog.category || "Uncategorized";

  // Extract featured image URL from blog.images array
  // Backend stores Cloudinary URLs in blog_images table like: "https://res.cloudinary.com/dqxpvhrin/image/upload/v1754221873/samples/people/boy-snow-hoodie.jpg"
  // Images can be returned as array of objects with 'image' property or array of URL strings
  let featuredImage = null;
  
  if (blog.images && Array.isArray(blog.images) && blog.images.length > 0) {
    const firstImage = blog.images[0];
    
    // Handle array of objects: [{image: "https://res.cloudinary.com/..."}, ...]
    if (typeof firstImage === 'object' && firstImage !== null && !Array.isArray(firstImage)) {
      // Check for 'image' property (most common from database)
      if (firstImage.image && typeof firstImage.image === 'string' && firstImage.image.trim()) {
        featuredImage = firstImage.image.trim();
      }
      // Check for 'url' property as fallback
      else if (firstImage.url && typeof firstImage.url === 'string' && firstImage.url.trim()) {
        featuredImage = firstImage.url.trim();
      }
    }
    // Handle array of strings: ["https://res.cloudinary.com/...", ...]
    else if (typeof firstImage === 'string' && firstImage.trim()) {
      featuredImage = firstImage.trim();
    }
  }
  
  // Fallback to blog.image if it exists (direct property, could be Cloudinary URL)
  if (!featuredImage && blog.image && typeof blog.image === 'string' && blog.image.trim()) {
    featuredImage = blog.image.trim();
  }
  
  // Validate image URL - must be a valid Cloudinary URL (https://) or relative path (/)
  // Cloudinary URLs format: https://res.cloudinary.com/{cloud_name}/image/upload/{version}/{path}
  if (!featuredImage || featuredImage === "" || (!featuredImage.startsWith('http://') && !featuredImage.startsWith('https://') && !featuredImage.startsWith('/'))) {
    featuredImage = "/placeholder.svg";
  }

  // Format publish date
  const publishDate = blog.createdAt || blog.publishDate || new Date().toISOString();

  // Calculate read time (rough estimate: 200 words per minute)
  const wordCount = blog.content ? blog.content.replace(/<[^>]*>/g, " ").split(/\s+/).length : 0;
  const readTime = Math.max(1, Math.ceil(wordCount / 200));

  return {
    id: blog.id,
    title: blog.title,
    slug: blog.slug,
    content: blog.content || "",
    excerpt: blog.metaDescription || blog.excerpt || "",
    author: blog.author || "Admin",
    publishDate: publishDate,
    category: category,
    tags: tags,
    featuredImage: featuredImage,
    readTime: `${readTime} min read`,
    views: blog.views || 0,
    likes: blog.likes || 0,
    metaTitle: blog.metaTitle || blog.title,
    metaDescription: blog.metaDescription || blog.excerpt || "",
    comments: blog.comments || [],
  };
}

// Normalize comment data
export function normalizeComment(comment) {
  return {
    id: comment.id,
    author: comment.name || comment.author || "Anonymous",
    avatar: comment.avatar || "/placeholder.svg",
    content: comment.comment || comment.content || "",
    date: comment.createdAt || comment.date || new Date().toISOString(),
    likes: comment.likes || 0,
    replies: comment.replies ? comment.replies.map(normalizeComment) : [],
    isAdmin: comment.isAdmin || false,
    parentId: comment.parentId || null,
  };
}

// Normalize comment for admin list display
export function normalizeCommentForAdmin(comment) {
  // Map backend status (approved, deleted) to frontend status
  // approved: 1, deleted: 0 → "approved"
  // approved: 0, deleted: 0 → "pending"
  // deleted: 1 → "spam" (deleted comments are treated as spam)
  let status = "pending";
  if (comment.deleted === 1) {
    status = "spam";
  } else if (comment.approved === 1) {
    status = "approved";
  } else if (comment.approved === 0) {
    status = "pending";
  }

  // Generate slug from blog title for linking
  const blogSlug = comment.blogTitle
    ? comment.blogTitle
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .trim()
    : "";

  return {
    id: comment.id,
    author: comment.name || "Anonymous",
    email: comment.email || "",
    avatar: "/placeholder.svg",
    content: comment.comment || "",
    blogPost: comment.blogTitle || "Unknown Blog",
    blogSlug: blogSlug,
    blogId: comment.blogId,
    status: status,
    date: comment.createdAt || new Date().toISOString(),
    likes: 0, // Backend doesn't return likes
    replies: comment.replies ? comment.replies.length : 0,
    isAdmin: comment.isAdmin || false,
    parentId: comment.parentId || null,
    approved: comment.approved || 0,
    deleted: comment.deleted || 0,
    moderatedBy: comment.moderatedBy || null,
    moderatedAt: comment.moderatedAt || null,
  };
}

// Flatten comments with replies into a single array for display
export function flattenCommentsWithReplies(comments) {
  const flattened = [];
  
  function flatten(comment) {
    // Add the comment itself
    flattened.push(comment);
    
    // Add all replies recursively
    if (comment.replies && Array.isArray(comment.replies)) {
      comment.replies.forEach(reply => {
        flatten(reply);
      });
    }
  }
  
  comments.forEach(comment => {
    flatten(comment);
  });
  
  return flattened;
}
